/**
 * BRAND CONFIGURATION
 * 
 * This file controls all branding and design system tokens for the viewer.
 * Update this file to white-label the application for different clients.
 * 
 * QUICK CUSTOMIZATION GUIDE:
 * 
 * 1. COMPANY & PRODUCT
 *    - companyName: Your company name
 *    - productName: The product being assembled
 *    - logoImageUrl: Path to your logo image
 * 
 * 2. COLORS
 *    - colors.accent: Primary brand color (used for buttons, highlights, progress)
 *    - colors.primary/primaryDark: Additional brand colors
 *    - colors.danger: Error/warning color
 * 
 * 3. DESIGN SYSTEM
 *    - theme.backgroundColor: Modal/panel backgrounds
 *    - theme.primaryTextColor: Main text color
 *    - theme.secondaryTextColor: Muted/secondary text
 * 
 * All UI components automatically inherit these values.
 */

export interface BrandConfig {
  // Company & Product
  companyName: string;
  productName: string;
  productSubtitle: string;
  tagline: string;
  
  // Logo
  logoIcon: string;
  logoType: 'icon' | 'image' | 'svg';
  logoImageUrl?: string;
  
  // Brand Colors
  colors: {
    primary: string;
    primaryDark: string;
    secondary: string;
    danger: string;
    accent: string;
  };
  
  // Design System Tokens
  theme: {
    // Modal/Panel backgrounds
    backgroundColor: string;
    backdropBlur: string;
    borderColor: string;
    boxShadow: string;
    
    // Text colors
    primaryTextColor: string;
    secondaryTextColor: string;
    
    // Other
    iconColor: string;
    headerBackgroundColor: string;
  };
  
  // Typography
  typography: {
    fontFamily: string;
  };
  
  // Component-specific defaults (panels, toolbar, controls)
  componentDefaults: {
    // Panels (Tools, Parts) and Toolbar
    panelBackgroundColor: string;
    panelBackdropBlur: string;
    panelBorderColor: string;
    panelBoxShadow: string;
    panelTitleColor: string;
    panelTextColor: string;
    panelMutedTextColor: string;
    panelIconColor: string;
    panelActiveColor: string;
    
    // Toolbar specific
    toolbarIconColor: string;
    toolbarActiveIconColor: string;
    toolbarActiveBackgroundColor: string;
    toolbarActiveBorderColor: string;
    toolbarDividerColor: string;
    
    // Step Controls
    stepControlsBackgroundColor: string;
    stepControlsBackdropBlur: string;
    stepControlsBorderColor: string;
    stepControlsBoxShadow: string;
    stepControlsTextColor: string;
    stepControlsIconColor: string;
    stepControlsPrimaryButtonColor: string;
    stepControlsPrimaryButtonTextColor: string;
    stepControlsProgressBarColor: string;
    stepControlsProgressBarBackgroundColor: string;
  };
}

export const BRAND_CONFIG: BrandConfig = {
  // Company & Product Identity
  companyName: 'Weijia Construction',
  productName: 'Sunshade Louver Pergola',
  productSubtitle: 'Manual',
  tagline: 'High-Precision Assembly Guide',
  
  // Logo Configuration
  logoIcon: 'view_in_ar',
  logoType: 'image',
  logoImageUrl: '/weijia-construction-logo.png',
  
  // Brand Colors
  colors: {
    primary: '#38bdf8',      // Sky-400
    primaryDark: '#0ea5e9',  // Sky-500
    secondary: '#6366f1',    // Indigo-500
    danger: '#f43f5e',       // Rose-500
    accent: 'rgb(56, 189, 248)', // Primary in RGB format
  },
  
  // Design System Tokens
  theme: {
    // Glass morphism
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    backdropBlur: '40px',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    
    // Text
    primaryTextColor: 'rgba(255, 255, 255, 0.95)',
    secondaryTextColor: 'rgba(255, 255, 255, 0.7)',
    
    // UI elements
    iconColor: 'rgba(255, 255, 255, 0.8)',
    headerBackgroundColor: 'rgba(55, 55, 55, 0.85)',
  },
  
  // Typography
  typography: {
    fontFamily: 'Arial, sans-serif',
  },
  
  // Component-specific defaults (single source of truth for all UI components)
  componentDefaults: {
    // Panels (Tools, Parts) and Toolbar - shared styling
    panelBackgroundColor: 'rgba(55, 55, 55, 0.85)',
    panelBackdropBlur: '24px',
    panelBorderColor: 'rgba(255, 255, 255, 0.12)',
    panelBoxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
    panelTitleColor: 'rgb(56, 189, 248)',
    panelTextColor: 'rgba(255, 255, 255, 0.9)',
    panelMutedTextColor: 'rgba(255, 255, 255, 0.45)',
    panelIconColor: 'rgba(255, 255, 255, 0.75)',
    panelActiveColor: 'rgb(56, 189, 248)',
    
    // Toolbar specific
    toolbarIconColor: 'rgba(255, 255, 255, 0.8)',
    toolbarActiveIconColor: 'rgba(255, 255, 255, 0.8)',
    toolbarActiveBackgroundColor: 'rgba(56, 189, 248, 0.15)',
    toolbarActiveBorderColor: 'rgba(56, 189, 248, 0.3)',
    toolbarDividerColor: 'rgba(255, 255, 255, 0.2)',
    
    // Step Controls
    stepControlsBackgroundColor: 'rgba(55, 55, 55, 0.85)',
    stepControlsBackdropBlur: '24px',
    stepControlsBorderColor: 'rgba(255, 255, 255, 0.12)',
    stepControlsBoxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4)',
    stepControlsTextColor: 'rgba(255, 255, 255, 0.9)',
    stepControlsIconColor: 'rgba(255, 255, 255, 0.8)',
    stepControlsPrimaryButtonColor: 'rgb(255, 255, 255)',
    stepControlsPrimaryButtonTextColor: 'rgba(15, 23, 42, 0.95)',
    stepControlsProgressBarColor: 'rgb(56, 189, 248)',
    stepControlsProgressBarBackgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
};

export type ViewerThemeMode = 'dark' | 'light';

export interface BrandThemeTokens {
  theme: BrandConfig['theme'];
  componentDefaults: BrandConfig['componentDefaults'];
}

export function resolveBrandTokens(config: BrandConfig, mode: ViewerThemeMode): BrandThemeTokens {
  if (mode === 'dark') {
    return {
      theme: config.theme,
      componentDefaults: config.componentDefaults,
    };
  }

  return {
    theme: {
      ...config.theme,
      backgroundColor: 'rgba(255, 255, 255, 0.88)',
      borderColor: 'rgba(15, 23, 42, 0.16)',
      boxShadow: '0 24px 48px -18px rgba(15, 23, 42, 0.35)',
      primaryTextColor: 'rgba(15, 23, 42, 0.92)',
      secondaryTextColor: 'rgba(15, 23, 42, 0.68)',
      iconColor: 'rgba(15, 23, 42, 0.78)',
      headerBackgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    componentDefaults: {
      ...config.componentDefaults,
      panelBackgroundColor: 'rgba(255, 255, 255, 0.88)',
      panelBorderColor: 'rgba(15, 23, 42, 0.14)',
      panelBoxShadow: '0 20px 40px -20px rgba(15, 23, 42, 0.25)',
      panelTextColor: 'rgba(15, 23, 42, 0.9)',
      panelMutedTextColor: 'rgba(15, 23, 42, 0.58)',
      panelIconColor: 'rgba(15, 23, 42, 0.78)',
      toolbarIconColor: 'rgba(15, 23, 42, 0.78)',
      toolbarActiveIconColor: 'rgba(15, 23, 42, 0.92)',
      toolbarActiveBackgroundColor: 'rgba(56, 189, 248, 0.22)',
      toolbarActiveBorderColor: 'rgba(56, 189, 248, 0.38)',
      toolbarDividerColor: 'rgba(15, 23, 42, 0.16)',
      stepControlsBackgroundColor: 'rgba(255, 255, 255, 0.88)',
      stepControlsBorderColor: 'rgba(15, 23, 42, 0.14)',
      stepControlsBoxShadow: '0 20px 40px -20px rgba(15, 23, 42, 0.25)',      stepControlsTextColor: 'rgba(15, 23, 42, 0.95)',      stepControlsIconColor: 'rgba(15, 23, 42, 0.78)',
      stepControlsPrimaryButtonColor: 'rgba(15, 23, 42, 0.9)',
      stepControlsPrimaryButtonTextColor: 'rgba(255, 255, 255, 0.95)',
      stepControlsProgressBarBackgroundColor: 'rgba(15, 23, 42, 0.14)',
    },
  };
}
